import { useEffect } from 'react'

/*
 * Holds the page still while an overlay is open.
 *
 * `overflow: hidden` on html/body is the usual way and it is not enough here:
 * the app zooms its container (`.mainContainer`, zoom 0.9), and with that in
 * play Chrome still scrolls the root on a wheel event — the page crept behind
 * every modal by however much it overflowed.
 *
 * So the body is pinned instead: taken out of flow at its current offset, which
 * cannot scroll at all, and put back — at the same offset — when the overlay
 * closes. The overflow rules stay for browsers where they do work, and for the
 * horizontal axis.
 */
export const useDisableScroll = (shouldDisable: boolean) => {
  useEffect(() => {
    if (!shouldDisable) {
      return undefined
    }

    const { body, documentElement: html } = document
    const scrollY = window.scrollY

    const original = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      left: body.style.left,
      position: body.style.position,
      right: body.style.right,
      top: body.style.top,
      width: body.style.width,
    }

    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    // Pinned where the reader left it, so opening an overlay does not jump the
    // page to the top behind it.
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'

    return () => {
      html.style.overflow = original.htmlOverflow
      body.style.overflow = original.bodyOverflow
      body.style.position = original.position
      body.style.top = original.top
      body.style.left = original.left
      body.style.right = original.right
      body.style.width = original.width
      // Fixed positioning lost the scroll offset; hand it back so closing an
      // overlay leaves the reader where they were.
      window.scrollTo(0, scrollY)
    }
  }, [shouldDisable])
}
